import { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'dark' | 'light' | 'system'
type Accent = 'default' | 'blue' | 'green' | 'orange' | 'red' | 'rose' | 'violet' | 'yellow'

type ThemeProviderProps = {
	children: React.ReactNode
	defaultTheme?: Theme
	storageKey?: string
	accentStorageKey?: string
}

type ThemeProviderState = {
	theme: Theme
	setTheme: (theme: Theme) => void
	accent: Accent
	setAccent: (accent: Accent) => void
}

const initialState: ThemeProviderState = {
	theme: 'system',
	setTheme: () => null,
	accent: 'default',
	setAccent: () => null
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
	children,
	defaultTheme = 'system',
	storageKey = 'vite-ui-theme',
	accentStorageKey = 'vite-ui-accent',
	...props
}: ThemeProviderProps) {
	const getSystemTheme = (): 'dark' | 'light' =>
		window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

	const [themeSetting, setThemeSetting] = useState<Theme>(
		() => (localStorage.getItem(storageKey) as Theme) || defaultTheme
	)

	const [theme, setResolvedTheme] = useState<Exclude<Theme, 'system'>>(() =>
		themeSetting === 'system' ? getSystemTheme() : themeSetting
	)

	const [accent, setAccentState] = useState<Accent>(
		() => (localStorage.getItem(accentStorageKey) as Accent) || 'default'
	)

	useEffect(() => {
		const root = window.document.documentElement

		root.classList.remove('light', 'dark')

		root.classList.add(theme)
	}, [theme])

	// Apply accent to root attribute for CSS variables
	useEffect(() => {
		const root = window.document.documentElement
		if (accent && accent !== 'default') {
			root.setAttribute('data-accent', accent)
		} else {
			root.removeAttribute('data-accent')
		}
	}, [accent])

	// Sync resolved theme when the setting changes
	useEffect(() => {
		if (themeSetting === 'system') {
			setResolvedTheme(getSystemTheme())
		} else {
			setResolvedTheme(themeSetting)
		}
	}, [themeSetting])

	// React to system theme changes when in 'system' mode
	useEffect(() => {
		if (themeSetting !== 'system') return
		const mql = window.matchMedia('(prefers-color-scheme: dark)')

		const handler = () => setResolvedTheme(mql.matches ? 'dark' : 'light')

		// Modern browsers
		if (typeof mql.addEventListener === 'function') {
			mql.addEventListener('change', handler)
			return () => mql.removeEventListener('change', handler)
		}

		// Fallback for older browsers
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-expect-error
		if (typeof (mql as never)?.addListener === 'function') {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-expect-error
			;(mql as never)?.addListener(handler)
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-expect-error
			return () => (mql as never)?.removeListener(handler)
		}
	}, [themeSetting])

	const value = {
		theme,
		setTheme: (nextTheme: Theme) => {
			localStorage.setItem(storageKey, nextTheme)
			setThemeSetting(nextTheme)
			setResolvedTheme(nextTheme === 'system' ? getSystemTheme() : nextTheme)
		},
		accent,
		setAccent: (nextAccent: Accent) => {
			localStorage.setItem(accentStorageKey, nextAccent)
			setAccentState(nextAccent)
		}
	}

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
	const context = useContext(ThemeProviderContext)

	if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider')

	return context
}
